/**
 * Peripheral Device Detection Actions
 * Handles detection, monitoring, and state updates for peripheral devices
 */

import {
  SET_PERIPHERAL_RESTRICTION_ACTIVE,
  SET_PERIPHERAL_ERROR,
  SET_DETECTED_DEVICES,
  SET_HAS_VIOLATING_DEVICES,
  SET_CHECKING_DEVICES,
  SET_LAST_CHECK_TIME,
  RESET_PERIPHERAL,
} from "./actionTypes";

import {
  detectAllPeripheralDevices,
  requestAllPermissions,
} from "../../utils/peripheral.util";

// Track peripheral detection state outside of Redux
let peripheralDetectionActive = false;
let peripheralCheckInterval = null;

/**
 * Action Creators
 */
export const setPeripheralRestrictionActive = (isActive) => ({
  type: SET_PERIPHERAL_RESTRICTION_ACTIVE,
  payload: isActive,
});

export const setPeripheralError = (error) => ({
  type: SET_PERIPHERAL_ERROR,
  payload: error,
});

export const setDetectedDevices = (devices) => ({
  type: SET_DETECTED_DEVICES,
  payload: devices,
});

export const setHasViolatingDevices = (hasViolations) => ({
  type: SET_HAS_VIOLATING_DEVICES,
  payload: hasViolations,
});

export const setCheckingDevices = (isChecking) => ({
  type: SET_CHECKING_DEVICES,
  payload: isChecking,
});

export const setLastCheckTime = (timestamp) => ({
  type: SET_LAST_CHECK_TIME,
  payload: timestamp,
});

export const resetPeripheral = () => {
  // Clean up intervals
  if (peripheralCheckInterval) {
    clearInterval(peripheralCheckInterval);
    peripheralCheckInterval = null;
  }
  peripheralDetectionActive = false;

  return {
    type: RESET_PERIPHERAL,
  };
};

/**
 * Start peripheral device detection (one-time scan)
 */
export const startPeripheralDetection = () => async (dispatch, getState) => {
  // Prevent multiple simultaneous detections
  if (peripheralDetectionActive) {
    return true;
  }

  peripheralDetectionActive = true;

  try {
    dispatch(setCheckingDevices(true));
    dispatch(setPeripheralError(null));

    console.log("🔑 Starting simplified peripheral detection...");

    // Request all required permissions automatically
    const permissionResult = await requestAllPermissions();

    if (!permissionResult.hasAnyPermission) {
      throw new Error(
        "PERMISSIONS_DENIED: User denied required permissions for peripheral device detection."
      );
    }

    // Simple detection - count all device types
    const detectedDevices = await detectAllPeripheralDevices();
    const hasViolatingDevices =
      Array.isArray(detectedDevices) && detectedDevices.length > 0;

    dispatch(setDetectedDevices(detectedDevices));
    dispatch(setHasViolatingDevices(hasViolatingDevices));
    dispatch(setLastCheckTime(Date.now()));
    dispatch(setPeripheralRestrictionActive(true));

    peripheralDetectionActive = false;
    dispatch(setCheckingDevices(false));

    if (hasViolatingDevices) {
      console.log(
        "❌ Peripheral device violations found - blocking user progress"
      );
      dispatch(
        setPeripheralError({
          name: "PeripheralDeviceViolation",
          message:
            "External peripheral devices detected. Please disconnect all external devices to continue.",
        })
      );
      return false; // Block progress when violations found
    }

    console.log("✅ Peripheral device detection completed successfully");
    return true; // Allow progress only when no violations
  } catch (error) {
    peripheralDetectionActive = false;
    dispatch(setCheckingDevices(false));

    console.error("❌ Peripheral device detection failed:", error);

    // Handle permission denial specifically
    if (error.message && error.message.includes("PERMISSIONS_DENIED")) {
      dispatch(
        setPeripheralError({
          name: "PeripheralPermissionDenied",
          message:
            "Peripheral device detection failed: User denied required permissions. HID/USB permissions are mandatory for peripheral device monitoring. Please grant permissions to continue.",
        })
      );
    } else {
      dispatch(
        setPeripheralError({
          name: error.name || "PeripheralDetectionError",
          message: error.message || "Failed to detect peripheral devices",
        })
      );
    }
    return false;
  }
};

/**
 * Recheck peripheral devices (manual re-check)
 */
export const recheckPeripheralDevices = () => async (dispatch) => {
  try {
    dispatch(setCheckingDevices(true));
    dispatch(setPeripheralError(null));

    // Just run detection without requesting permissions again
    const detectedDevices = await detectAllPeripheralDevices();
    const hasViolatingDevices =
      Array.isArray(detectedDevices) && detectedDevices.length > 0;

    dispatch(setDetectedDevices(detectedDevices));
    dispatch(setHasViolatingDevices(hasViolatingDevices));
    dispatch(setLastCheckTime(Date.now()));
    dispatch(setCheckingDevices(false));

    if (hasViolatingDevices) {
      dispatch(
        setPeripheralError({
          name: "PeripheralDeviceViolation",
          message:
            "External peripheral devices detected. Please disconnect all external devices to continue.",
        })
      );
      return false;
    }

    return true;
  } catch (error) {
    dispatch(setCheckingDevices(false));
    dispatch(
      setPeripheralError({
        name: error.name || "PeripheralDetectionError",
        message: error.message || "Failed to detect peripheral devices",
      })
    );
    return false;
  }
};

/**
 * Stop peripheral device detection and monitoring
 */
export const stopPeripheralDetection = () => async (dispatch) => {
  peripheralDetectionActive = false;

  // Clean up all monitoring
  if (peripheralCheckInterval) {
    clearInterval(peripheralCheckInterval);
    peripheralCheckInterval = null;
  }

  dispatch(setPeripheralRestrictionActive(false));
  dispatch(setDetectedDevices([]));
  dispatch(setHasViolatingDevices(false));
  dispatch(setPeripheralError(null));
  dispatch(setCheckingDevices(false));
};
